/**
 * Sourcing Calls — designer-facing post & track page, supplier-facing feed.
 *
 * The same page handles both audiences (`/sourcing`):
 *   • Verified designers see a "Post a Sourcing Call" CTA + their own posted
 *     calls with reply counts.
 *   • Suppliers see an "Open Calls" feed and can reply in an open thread.
 *   • Unverified users see an "Apply as designer" CTA.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Loader2, Megaphone, Plus, Send, Clock, MapPin, Phone, ChevronRight, Check,
} from 'lucide-react';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogTrigger } from '../components/ui/dialog';
import { devError } from '../utils/logger';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const ADMIN_EMAILS = ['thisisradas@gmail.com'];

function isAdmin(user) {
  return !!user && (user.user_type === 'admin' || (user.email && ADMIN_EMAILS.includes(user.email.toLowerCase())));
}
function isVerifiedDesigner(user) {
  return !!user && (user.verified_designer === true || isAdmin(user));
}

export default function SourcingPage() {
  const { user, loading: authLoading } = useAuth();

  if (authLoading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-accent animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2 inline-flex items-center gap-2">
            <Megaphone className="w-3 h-3" /> Sourcing Calls
          </p>
          <h1 className="font-playfair text-4xl md:text-5xl font-bold mb-3">
            "Anyone got…?" — broadcast to every supplier at once
          </h1>
          <p className="text-muted-foreground max-w-2xl">
            The modern equivalent of the SA car-parts CB-radio network. Verified designers post a
            sourcing call → every PatinaMart supplier sees it instantly via Wingman push and email →
            replies arrive in an open thread, then you pick the winner.
          </p>
        </div>

        {!user ? (
          <AnonymousFeed />
        ) : user.user_type === 'supplier' ? (
          <SupplierFeed />
        ) : isVerifiedDesigner(user) ? (
          <DesignerView />
        ) : (
          <ApplyAsDesigner user={user} />
        )}
      </div>
    </div>
  );
}

/** Publicly visible feed of open calls — no account needed to browse.
    Reply button swaps to a "Sign in to respond" link. */
function AnonymousFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/sourcing-calls/feed`);
        if (!cancelled) setItems(data.items || []);
      } catch (e) { devError('public feed', e); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-2xl">Open Sourcing Calls</h2>
        <Link to="/login?next=/sourcing" className="text-sm text-accent hover:underline" data-testid="sourcing-anon-signin">
          Sign in to respond →
        </Link>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>
      ) : items.length === 0 ? (
        <EmptyHero text="No open sourcing calls right now. Sign in as a designer to post one, or as a supplier to be notified when a new call drops." />
      ) : (
        <div className="space-y-4" data-testid="anonymous-feed">
          {items.map((c) => <CallRow key={c.call_id} call={c} anonymous />)}
        </div>
      )}
    </div>
  );
}

// ------------------------------- Designer ---------------------------------
function DesignerView() {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sourcing-calls/me`);
      setCalls(data.items || []);
    } catch (e) { devError('my calls', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-playfair text-2xl">Your sourcing calls</h2>
        <PostCallDialog onPosted={refresh} />
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>
      ) : calls.length === 0 ? (
        <EmptyHero text="You haven't posted a sourcing call yet — your first one will be broadcast to every supplier within seconds." />
      ) : (
        <div className="space-y-4" data-testid="my-calls-list">
          {calls.map((c) => <CallRow key={c.call_id} call={c} ownerView />)}
        </div>
      )}
    </div>
  );
}

function PostCallDialog({ onPosted }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Reply window in HOURS — defaults to 24h. "today" sentinel = 0; resolved
  // at submit-time to "hours until end of today (local)" so the call closes
  // at midnight tonight rather than a flat 24h block. Anything > 0 is sent
  // through verbatim as `reply_window_hours`.
  const [form, setForm] = useState({
    title: '', description: '', category: '', quantity: '', location: '',
    needed_by: '', reply_window_hours: 24,
  });

  const resolvedHours = (selected) => {
    if (selected !== 0) return selected;
    const end = new Date(); end.setHours(23, 59, 59, 999);
    const hours = Math.max(1, Math.ceil((end.getTime() - Date.now()) / 3600000));
    return hours;
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = { ...form, reply_window_hours: resolvedHours(form.reply_window_hours) };
      await axios.post(`${API}/sourcing-calls`, payload);
      toast.success('Sourcing call broadcast to every supplier');
      setOpen(false);
      setForm({ title: '', description: '', category: '', quantity: '', location: '', needed_by: '', reply_window_hours: 24 });
      onPosted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="post-call-trigger"><Plus className="w-4 h-4 mr-2" />Post a Sourcing Call</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="post-call-dialog">
        <form onSubmit={submit} className="space-y-4">
          <div>
            <h2 className="font-playfair text-2xl font-bold mb-1">Post a Sourcing Call</h2>
            <p className="text-xs text-muted-foreground">Broadcast to every supplier instantly. Replies arrive in an open thread you control.</p>
          </div>
          <Field label="What do you need? *">
            <Input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="50 sqm of 1900s baltic pine flooring" maxLength={140} data-testid="call-title" />
          </Field>
          <Field label="Description / context *">
            <Textarea required value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              rows={4} placeholder="Heritage extension, Adelaide, 130mm boards preferred, Tongue & groove…" maxLength={4000} data-testid="call-description" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category"><Input value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} placeholder="flooring" /></Field>
            <Field label="Quantity"><Input value={form.quantity} onChange={(e) => setForm((f) => ({ ...f, quantity: e.target.value }))} placeholder="50 sqm" /></Field>
            <Field label="Pickup location"><Input value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} placeholder="Adelaide" /></Field>
            <Field label="Needed by"><Input value={form.needed_by} onChange={(e) => setForm((f) => ({ ...f, needed_by: e.target.value }))} placeholder="6 weeks" /></Field>
          </div>
          <Field label="Reply window">
            <select
              value={form.reply_window_hours}
              onChange={(e) => setForm((f) => ({ ...f, reply_window_hours: Number(e.target.value) }))}
              className="w-full bg-background border border-border/40 rounded-md py-2 px-3 text-sm"
              data-testid="call-reply-window"
            >
              <option value={0}>Today (closes at midnight)</option>
              <option value={24}>24 hours (recommended)</option>
              <option value={72}>3 days</option>
              <option value={168}>7 days</option>
              <option value={336}>14 days</option>
            </select>
          </Field>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="call-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Broadcast
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------- Supplier ---------------------------------
function SupplierFeed() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/sourcing-calls/feed`);
      setItems(data.items || []);
    } catch (e) { devError('supplier feed', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  return (
    <div>
      <h2 className="font-playfair text-2xl mb-6">Open Sourcing Calls</h2>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-accent" /></div>
      ) : items.length === 0 ? (
        <EmptyHero text="No open calls right now — when a designer posts one, you'll get a Wingman push notification." />
      ) : (
        <div className="space-y-4" data-testid="supplier-feed">
          {items.map((c) => <CallRow key={c.call_id} call={c} supplierView onReplied={refresh} />)}
        </div>
      )}
    </div>
  );
}

// ------------------------------- Card -------------------------------------
function CallRow({ call, ownerView, supplierView, anonymous, onReplied }) {
  const deadline = useMemo(() => new Date(call.deadline_at), [call.deadline_at]);
  const remaining = useMemo(() => {
    const ms = deadline.getTime() - Date.now();
    if (ms <= 0) return 'closed';
    const days = Math.floor(ms / 86400000);
    const hours = Math.floor((ms % 86400000) / 3600000);
    const minutes = Math.floor((ms % 3600000) / 60000);
    if (days > 0) return `${days}d ${hours}h left`;
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
  }, [deadline]);

  return (
    <article className="rounded-xl border border-border/30 bg-card/30 p-5" data-testid={`call-row-${call.call_id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <h3 className="font-playfair text-xl font-semibold">{call.title}</h3>
        <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded ${
          call.status === 'open' ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' :
          call.status === 'fulfilled' ? 'bg-accent/15 text-accent border border-accent/30' :
          'bg-muted text-muted-foreground'
        }`}>{call.status}</span>
      </div>
      <p className="text-sm text-foreground/80 mb-3 line-clamp-3">{call.description}</p>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground mb-3">
        {call.category && <span>📦 {call.category}</span>}
        {call.quantity && <span>🔢 {call.quantity}</span>}
        {call.location && <span><MapPin className="w-3 h-3 inline mr-0.5" />{call.location}</span>}
        {call.needed_by && <span>⏰ Needed: {call.needed_by}</span>}
        <span><Clock className="w-3 h-3 inline mr-0.5" />{remaining}</span>
        {call.created_via === 'phone' && <span><Phone className="w-3 h-3 inline mr-0.5" />Phone concierge</span>}
      </div>
      <div className="flex items-center justify-between gap-3">
        <div className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
          <Send className="w-3 h-3" />
          {`${call.reply_count || 0} repl${call.reply_count === 1 ? 'y' : 'ies'}`}
        </div>
        {ownerView ? (
          <Link to={`/sourcing/${call.call_id}`} className="text-xs text-accent hover:underline inline-flex items-center gap-1" data-testid={`view-call-${call.call_id}`}>
            View replies <ChevronRight className="w-3 h-3" />
          </Link>
        ) : supplierView ? (
          call.already_replied ? (
            <span className="text-xs text-emerald-300 inline-flex items-center gap-1"><Check className="w-3 h-3" />Replied</span>
          ) : (
            <ReplyDialog call={call} onReplied={onReplied} />
          )
        ) : anonymous ? (
          <Link
            to="/login?next=/sourcing"
            className="text-xs text-accent hover:underline inline-flex items-center gap-1"
            data-testid={`anon-reply-${call.call_id}`}
          >
            Sign in to respond <ChevronRight className="w-3 h-3" />
          </Link>
        ) : (
          <Link to={`/sourcing/${call.call_id}`} className="text-xs text-accent hover:underline inline-flex items-center gap-1">
            View thread <ChevronRight className="w-3 h-3" />
          </Link>
        )}
      </div>
    </article>
  );
}

function ReplyDialog({ call, onReplied }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ message: '', price_aud: '' });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/sourcing-calls/${call.call_id}/replies`, {
        message: form.message,
        price_aud: form.price_aud ? Number(form.price_aud) : null,
      });
      toast.success('Reply sent');
      setOpen(false);
      onReplied?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reply failed');
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid={`reply-trigger-${call.call_id}`}>Reply</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="reply-dialog">
        <form onSubmit={submit} className="space-y-4">
          <h2 className="font-playfair text-xl font-bold">Reply to "{call.title}"</h2>
          <p className="text-xs text-muted-foreground">Your reply joins an open thread — the buyer and other suppliers can read it. Be specific.</p>
          <Field label="What can you offer? *">
            <Textarea required value={form.message} onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              rows={5} placeholder="We have ~40 sqm of 130mm baltic pine pulled from a 1905 cottage. Photos available, $85/sqm…"
              minLength={10} maxLength={3000} data-testid="reply-message" />
          </Field>
          <Field label="Price (AUD, optional)">
            <Input type="number" min="0" step="0.01" value={form.price_aud}
              onChange={(e) => setForm((f) => ({ ...f, price_aud: e.target.value }))}
              placeholder="3400" data-testid="reply-price" />
          </Field>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={submitting}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="reply-submit">
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Send reply
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------- Apply ------------------------------------
function ApplyAsDesigner({ user }) {
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(user?.designer_application?.status === 'pending');
  const [form, setForm] = useState({ professional_name: user?.name || '', firm: '', portfolio_url: '', practice_summary: '' });
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/sourcing-calls/designers/apply`, form);
      setDone(true);
      toast.success("Application received — we'll review within a few days");
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not submit');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-8 text-center">
        <h2 className="font-playfair text-xl mb-2">Application received</h2>
        <p className="text-sm text-emerald-200/80">
          Thanks for applying. We review designer applications manually within a few business days.
          Once approved, you'll see the "Post a Sourcing Call" button right here.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="rounded-xl border border-border/30 bg-card/30 p-6 md:p-8 space-y-4 max-w-2xl">
      <div>
        <h2 className="font-playfair text-2xl mb-1">Apply as a verified designer</h2>
        <p className="text-xs text-muted-foreground">Sourcing Calls are limited to verified designers / architects to keep signal high. Tell us about your practice and we'll approve within a few days.</p>
      </div>
      <Field label="Your name *"><Input required value={form.professional_name} onChange={(e) => setForm((f) => ({ ...f, professional_name: e.target.value }))} data-testid="apply-name" /></Field>
      <Field label="Firm / studio"><Input value={form.firm} onChange={(e) => setForm((f) => ({ ...f, firm: e.target.value }))} data-testid="apply-firm" /></Field>
      <Field label="Portfolio URL"><Input type="url" value={form.portfolio_url} onChange={(e) => setForm((f) => ({ ...f, portfolio_url: e.target.value }))} placeholder="https://" data-testid="apply-portfolio" /></Field>
      <Field label="What kind of work do you do? *">
        <Textarea required value={form.practice_summary} onChange={(e) => setForm((f) => ({ ...f, practice_summary: e.target.value }))}
          rows={4} minLength={20} maxLength={2000} placeholder="Heritage residential renovations, focus on adaptive reuse, ~6 projects per year"
          data-testid="apply-summary" />
      </Field>
      <Button type="submit" disabled={submitting} data-testid="apply-submit">
        {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
        Submit application
      </Button>
    </form>
  );
}

function Field({ label, children }) {
  return <div><label className="block text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{label}</label>{children}</div>;
}

function EmptyHero({ text }) {
  return (
    <div className="rounded-xl border border-border/20 bg-card/30 py-16 px-6 text-center">
      <Megaphone className="w-10 h-10 text-accent/40 mx-auto mb-3" />
      <p className="text-muted-foreground max-w-md mx-auto">{text}</p>
    </div>
  );
}
